// frontend-react/src/components/keepers/EligiblePlayersList.js
import React, { useState } from 'react';
import { Plus, Minus, Search, DollarSign, Calendar } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { formatContractDisplay } from '../../services/contractUtils';
import { getStatConfigs, getStatValue, formatStatValue } from '../../utils/statMapping';
import KeeperPositionModal from './KeeperPositionModal';
import PlayerLink from '../PlayerLink';
import { isPitcherPosition } from '../../utils/playerClassification';

/**
 * Eligible Players List Component
 * Displays list of players eligible to be kept
 */
const EligiblePlayersList = ({
  eligibleKeepers,
  selectedPlayerIds,
  keeperAssignments,
  maxKeepers,
  league,
  loading,
  onAssignPosition,
  onRemoveKeeper
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [modalPlayer, setModalPlayer] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Resolve league scoring categories
  const scoringCategories = React.useMemo(() => {
    const cats = league?.league_settings?.scoring_categories;
    if (cats) {
      return {
        hitting: cats.hitting || [],
        pitching: cats.pitching || []
      };
    }
    return { hitting: [], pitching: [] };
  }, [league]);

  // Build deduplicated stat configs for hitters and pitchers
  const hitterStatConfigs = React.useMemo(() => {
    const labels = [...new Set(['G', 'AB', 'H', 'BB', ...(scoringCategories.hitting || [])])];
    return getStatConfigs(labels, false);
  }, [scoringCategories.hitting]);

  const pitcherStatConfigs = React.useMemo(() => {
    const labels = [...new Set(['G', 'GS', 'IP', ...(scoringCategories.pitching || [])])];
    return getStatConfigs(labels, true);
  }, [scoringCategories.pitching]);

  if (loading) {
    return (
      <div className={dynastyTheme.components.card.base + ' p-6'}>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className={`h-20 ${dynastyTheme.classes.bg.neutral} rounded`}></div>
          ))}
        </div>
      </div>
    );
  }

  // Filter eligible keepers based on search and position
  const filteredKeepers = eligibleKeepers.filter(keeper => {
    const playerName = keeper.info?.full_name || '';
    const matchesSearch = searchTerm === '' || 
      playerName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const position = keeper.info?.position || '';
    const matchesPosition = positionFilter === 'all' || 
      position === positionFilter;
    
    return matchesSearch && matchesPosition;
  });

  // Get unique positions for filter
  const positions = ['all', ...new Set(eligibleKeepers.map(k => k.info?.position || 'UTIL'))];

  // Helper: determine if player is pitcher
  const isPitcher = (keeper) => isPitcherPosition(keeper.info?.position);

  // Helper: extract category_ranks key from stat config field
  // config.field = 'hitting.home_runs' → 'home_runs' (matches category_ranks JSONB keys)
  const getRankKey = (config) => {
    const parts = config.field.split('.');
    return parts.length > 1 ? parts[1] : parts[0];
  };

  // Check if player is selected
  const isSelected = (playerId) => selectedPlayerIds.includes(playerId);

  // Check if we can add more keepers
  const canAddMore = selectedPlayerIds.length < maxKeepers;

  // Handle position assignment confirmation
  const handlePositionConfirm = (playerId, rosterPosition) => {
    onAssignPosition(playerId, rosterPosition);
    setShowModal(false);
    setModalPlayer(null);
  };

  // Handle select button click
  const handleSelectClick = (keeper) => {
    if (isSelected(keeper.player_id)) {
      // Already selected - remove
      onRemoveKeeper(keeper.player_id);
    } else {
      // Not selected - show position modal
      setModalPlayer(keeper);
      setShowModal(true);
    }
  };

  return (
    <div className={dynastyTheme.components.card.base + ' p-6'}>
      {/* Header */}
      <h3 className={dynastyTheme.components.heading.h3}>
        Eligible Keepers ({filteredKeepers.length})
      </h3>

      {/* Search and Filter Controls */}
      <div className="space-y-3 mb-6">
        {/* Search */}
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${dynastyTheme.classes.text.neutral}`} />
          <input
            type="text"
            placeholder="Search players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`
              ${dynastyTheme.components.input}
              pl-10 w-full
            `}
          />
        </div>

        {/* Position Filter */}
        <div className="flex flex-wrap gap-2">
          {positions.map(pos => (
            <button
              key={pos}
              onClick={() => setPositionFilter(pos)}
              className={`
                px-3 py-1 rounded-md text-sm font-medium
                ${positionFilter === pos
                  ? `${dynastyTheme.classes.bg.primary} text-black`
                  : `${dynastyTheme.classes.bg.neutral}/20 ${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.bg.neutral}/40`
                }
                ${dynastyTheme.classes.transition}
              `}
            >
              {pos === 'all' ? 'All' : pos}
            </button>
          ))}
        </div>
      </div>

      {/* Empty State */}
      {filteredKeepers.length === 0 && (
        <div className="text-center py-12">
          <p className={dynastyTheme.classes.text.neutral}>
            {searchTerm || positionFilter !== 'all' 
              ? 'No players match your filters'
              : 'No eligible keepers available'
            }
          </p>
        </div>
      )}

      {/* Players List */}
      <div className="space-y-1.5">
        {filteredKeepers.map(keeper => {
          const selected = isSelected(keeper.player_id);
          const disabled = !selected && !canAddMore;

          return (
            <div
              key={keeper.player_id}
              className={`
                flex items-center gap-3 px-3 py-2 rounded-md
                ${selected
                  ? 'border border-yellow-500/30 border-l-[3px] border-l-yellow-500'
                  : 'border border-neutral-700 hover:border-neutral-600'
                }
                bg-[#1c1917]
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              {/* Identity block — fixed width */}
              <div className="min-w-[180px] flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <PlayerLink mlbId={keeper.ids?.mlb} name={keeper.info?.full_name || 'Unknown Player'} style={{ fontSize: '0.875rem', fontWeight: 700, color: '#f5f5f5' }} />
                  <span className="text-[10px] font-semibold text-neutral-400 px-1.5 py-0 rounded border border-neutral-700 bg-neutral-800">
                    {keeper.info?.position || 'UTIL'}
                  </span>
                  <span className="text-neutral-500 text-[11px]">
                    {keeper.info?.mlb_team || ''}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-yellow-500 text-xs font-semibold">
                    ${Math.round(keeper.league_context?.financial?.contract_salary || keeper.league_context?.financial?.salary || 0)}
                  </span>
                  <span className="text-neutral-500 text-[11px]">
                    {formatContractDisplay(keeper.league_context?.financial?.contract_type, keeper.league_context?.financial?.contract_years)}
                  </span>
                  {selected && (
                    <span className="text-emerald-500 text-[10px] font-semibold">✓ KEEPER</span>
                  )}
                </div>
              </div>

              {/* Stats block — fills remaining space */}
              {(() => {
                const pitcher = isPitcher(keeper);
                const configs = pitcher ? pitcherStatConfigs : hitterStatConfigs;
                const hasStats = keeper.stats?.prior_season && Object.keys(keeper.stats.prior_season).length > 0;

                if (!hasStats || configs.length === 0) return <div className="flex-1" />;

                return (
                  <div className="flex flex-1 bg-[#1f1c19] rounded py-1 px-0.5">
                    {configs.map((config, i) => {
                      const value = getStatValue(keeper, config, 'prior_season');
                      const rankKey = getRankKey(config);
                      const rank = keeper.rankings?.category_ranks?.[rankKey];
                      return (
                        <div
                          key={config.field}
                          className={`flex-1 text-center min-w-0 ${i < configs.length - 1 ? 'border-r border-neutral-700' : ''}`}
                        >
                          <div className="text-yellow-500 text-[11px] font-semibold tracking-wide">
                            {config.label}
                          </div>
                          <div className="text-neutral-100 text-[13px] font-bold leading-tight">
                            {formatStatValue(value, config)}
                          </div>
                          <div className="text-yellow-500 text-[11px] font-semibold">
                            {rank != null ? `#${rank}` : '\u00A0'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Select/Remove button */}
              <button
                onClick={() => handleSelectClick(keeper)}
                disabled={disabled}
                className={`
                  flex-shrink-0 text-[11px] font-semibold px-3 py-1 rounded cursor-pointer
                  pointer-events-auto
                  ${selected
                    ? 'bg-red-500 text-white border-none'
                    : disabled
                    ? 'bg-neutral-800/20 text-neutral-500 cursor-not-allowed'
                    : 'bg-transparent border border-neutral-700 text-neutral-400 hover:border-neutral-500'
                  }
                `}
              >
                {selected ? '— Remove' : '+ Select'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Position Modal */}
      <KeeperPositionModal
        player={modalPlayer}
        league={league}
        currentAssignments={keeperAssignments || {}}
        maxKeepers={maxKeepers}
        selectedCount={selectedPlayerIds.length}
        onConfirm={handlePositionConfirm}
        onCancel={() => {
          setShowModal(false);
          setModalPlayer(null);
        }}
        isVisible={showModal}
      />
    </div>
  );
};

export default EligiblePlayersList;
